import { API_BASE_URL } from '@/config/api'
import { getJsonAuthHeaders } from '@/lib/apiHeaders'
import { formatNextSendTimeGMT2 } from './nextSendFormatter'
import type {
  ReminderSettings,
  ReminderSettingsResponse,
  ReminderChannel,
  ReminderRecord,
  ReminderHistoryEntry
} from '@weekly-report/shared-types'

/**
 * Get authentication headers for API requests (with CSRF protection)
 */
const getAuthHeaders = getJsonAuthHeaders

/**
 * Response type for remind-now endpoint
 */
interface RemindNowResponse {
  success: boolean
  records: ReminderRecord[]
  summary: {
    sent: number
    failed: number
    total: number
  }
}

/**
 * Response type for reminder history endpoint
 */
interface ReminderHistoryResponse {
  history: ReminderHistoryEntry[]
  count: number
}

/**
 * Scheduler status response type
 */
interface SchedulerStatusResponse {
  enabled: boolean
  metrics: {
    remindersQueuedTotal: number
    remindersSentTotal: number
    remindersFailedTotal: number
    lastRunAt?: string
    lastRunDurationMs?: number
  }
  enabledUsersCount: number
  config: {
    timezone: string
    maxRetries: number
    cronExpression: string
  }
}

/**
 * Reminder Service
 * 
 * Provides client-side API methods for managing user reminder settings
 * and triggering reminders.
 */
export const reminderService = {
  /**
   * Get current user's reminder settings
   */
  async getSettings(): Promise<ReminderSettingsResponse> {
    const response = await fetch(`${API_BASE_URL}/users/me/reminder-settings`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to get reminder settings: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Update current user's reminder settings
   */
  async updateSettings(settings: Partial<ReminderSettings>): Promise<ReminderSettingsResponse> {
    const response = await fetch(`${API_BASE_URL}/users/me/reminder-settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(settings)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to update reminder settings: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Trigger an immediate reminder for the current user
   */
  async remindNow(channels?: ReminderChannel[]): Promise<RemindNowResponse> {
    const response = await fetch(`${API_BASE_URL}/users/me/remind-now`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channels })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      
      // Handle specific error codes
      if (error.code === 'REMINDERS_DISABLED') {
        throw new Error('Reminders are currently disabled by the administrator.')
      }
      if (error.code === 'USER_REMINDERS_DISABLED') {
        throw new Error('Please enable reminders in your settings first.')
      }
      
      throw new Error(error.error || `Failed to send reminder: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get reminder delivery history for the current user
   */
  async getHistory(limit?: number): Promise<ReminderHistoryResponse> {
    const url = new URL(`${API_BASE_URL}/users/me/reminder-history`, window.location.origin)
    if (limit) {
      url.searchParams.set('limit', String(limit))
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to get reminder history: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Admin: Get scheduler status and metrics
   */
  async getSchedulerStatus(): Promise<SchedulerStatusResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/reminders/scheduler/status`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to get scheduler status: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Admin: Manually trigger a scheduler run
   */
  async triggerSchedulerRun(): Promise<{ success: boolean; results: any }> {
    const response = await fetch(`${API_BASE_URL}/admin/reminders/scheduler/run`, {
      method: 'POST',
      headers: getAuthHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to trigger scheduler run: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get list of available timezones
   */
  getCommonTimezones(): { value: string; label: string }[] {
    return [
      { value: 'UTC', label: 'UTC' },
      { value: 'Asia/Jerusalem', label: 'Israel (IST)' }
    ]
  },

  /**
   * Get day of week options
   */
  getDayOfWeekOptions(): { value: number; label: string }[] {
    return [
      { value: 0, label: 'Sunday' },
      { value: 1, label: 'Monday' },
      { value: 2, label: 'Tuesday' },
      { value: 3, label: 'Wednesday' },
      { value: 4, label: 'Thursday' },
      { value: 5, label: 'Friday' },
      { value: 6, label: 'Saturday' }
    ]
  },

  /**
   * Get browser's timezone
   */
  getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return 'UTC'
    }
  },

  /**
   * Format next send time for display
   * Always displays in GMT+2 regardless of user's timezone setting
   * @param isoString - ISO string of the next send time (in UTC)
   * @param timezone - User's timezone setting (used to determine correct conversion)
   */
  formatNextSendTime: formatNextSendTimeGMT2
}
